import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Minus, Plus, ShoppingBag, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { formatDkk } from "@/lib/site";

export default function CartDrawer() {
  const { items, open, setOpen, setQty, remove, total } = useCart();
  const navigate = useNavigate();

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-[90] bg-ink/70 backdrop-blur-sm"
          />
          <motion.aside
            data-testid="cart-drawer"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="fixed right-0 top-0 z-[95] flex h-screen w-full max-w-md flex-col border-l border-white/10 bg-moss"
          >
            <div className="flex items-center justify-between border-b border-white/10 px-6 py-5">
              <p className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.25em] text-cream">
                <ShoppingBag className="h-4 w-4 text-forest" />
                Din kurv
              </p>
              <button
                data-testid="cart-close-button"
                onClick={() => setOpen(false)}
                className="text-sage transition-colors hover:text-cream"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-6">
              {items.length === 0 && (
                <p data-testid="cart-empty" className="text-sm text-sage">
                  Din kurv er tom.
                </p>
              )}
              <div className="space-y-5">
                {items.map((item) => (
                  <div
                    key={item.variant ? `${item.slug}__${item.variant}` : item.slug}
                    data-testid={`cart-item-${item.slug}`}
                    className="flex gap-4 border-b border-white/10 pb-5"
                  >
                    <img
                      src={item.image}
                      alt={item.name}
                      className="h-20 w-20 border border-white/10 object-cover"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium leading-snug text-cream">{item.name}</p>
                      {item.variant && (
                        <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.15em] text-sage">
                          {item.variant}
                        </p>
                      )}
                      <p className="mt-1 font-mono text-xs text-pale">
                        {formatDkk(item.price_dkk)}
                      </p>
                      <div className="mt-3 flex items-center gap-3">
                        <button
                          data-testid={`cart-minus-${item.slug}`}
                          onClick={() => setQty(item.variant ? `${item.slug}__${item.variant}` : item.slug, item.qty - 1)}
                          className="flex h-7 w-7 items-center justify-center border border-white/15 text-sage transition-colors hover:border-forest hover:text-cream"
                        >
                          <Minus className="h-3 w-3" />
                        </button>
                        <span className="font-mono text-xs text-cream">{item.qty}</span>
                        <button
                          data-testid={`cart-plus-${item.slug}`}
                          onClick={() => setQty(item.variant ? `${item.slug}__${item.variant}` : item.slug, item.qty + 1)}
                          className="flex h-7 w-7 items-center justify-center border border-white/15 text-sage transition-colors hover:border-forest hover:text-cream"
                        >
                          <Plus className="h-3 w-3" />
                        </button>
                        <button
                          data-testid={`cart-remove-${item.slug}`}
                          onClick={() => remove(item.variant ? `${item.slug}__${item.variant}` : item.slug)}
                          className="ml-auto font-mono text-[10px] uppercase tracking-[0.2em] text-sage transition-colors hover:text-cream"
                        >
                          Fjern
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {items.length > 0 && (
              <div className="border-t border-white/10 px-6 py-6">
                <div className="mb-5 flex items-center justify-between">
                  <span className="font-mono text-xs uppercase tracking-[0.25em] text-sage">
                    I alt
                  </span>
                  <span data-testid="cart-total" className="font-serif text-2xl text-cream">
                    {formatDkk(total)}
                  </span>
                </div>
                <button
                  data-testid="cart-checkout-button"
                  onClick={() => {
                    setOpen(false);
                    navigate("/kasse");
                  }}
                  className="group flex w-full items-center justify-center gap-3 rounded-full bg-forest py-4 font-mono text-xs uppercase tracking-[0.2em] text-cream transition-colors duration-300 hover:bg-pale hover:text-ink"
                >
                  Gå til kassen
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
